import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useParticipant } from '@videosdk.live/react-sdk';
import * as Kalidokit from 'kalidokit';
import '../components/vrm/vrm.css';
import avatarAssets from '../utils/avatarAssets';
import vrmLoader from '../utils/vrmLoader';

// Import Helper Functions from Kalidokit
const { clamp } = Kalidokit.Utils;
const { lerp } = Kalidokit.Vector;

const SingleVRMViewer = ({ 
  selectedAvatar = null, 
  isRemote = false, 
  participantId,
  VideoStreamComponent
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isMediaPipeInitializing, setIsMediaPipeInitializing] = useState(false);
  
  // Always call useParticipant hook (Rules of Hooks requirement)
  const participantData = useParticipant(participantId || "");
  
  // Create video stream based on whether this is remote or local VRM
  const videoStream = useMemo(() => {
    // For local VRM: use local participant's stream
    // For remote VRM: use remote participant's stream
    if (participantId && participantData && participantData.webcamOn && participantData.webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(participantData.webcamStream.track);
      console.log(`[VRM ISOLATION] ${isRemote ? 'REMOTE' : 'LOCAL'} VRM using participant ${participantId} (isLocal: ${participantData.isLocal})`);
      return mediaStream;
    }
    console.log(`[VRM ISOLATION] ${isRemote ? 'REMOTE' : 'LOCAL'} VRM has no stream - participantId: ${participantId}, hasData: ${!!participantData}, webcamOn: ${participantData?.webcamOn}`);
    return null;
  }, [participantId, participantData?.webcamStream, participantData?.webcamOn, isRemote]);
  
  // Camera stream assignment - this should be the stream for THIS specific VRM
  // For local VRM: should be local participant's camera
  // For remote VRM: should be remote participant's camera
  const cameraStream = useMemo(() => {
    // Use participant's stream if available
    if (videoStream) {
      console.log(`[VRM ISOLATION] ${isRemote ? 'REMOTE' : 'LOCAL'} VRM will animate with ${isRemote ? 'remote' : 'local'} participant's camera`);
      return videoStream;
    }
    
    console.log(`[VRM ISOLATION] ${isRemote ? 'REMOTE' : 'LOCAL'} VRM has no camera stream - will not animate`);
    return null;
  }, [videoStream, isRemote]);
  
  // Debug logging for stream assignment
  useEffect(() => {
    console.log(`[VRM ${isRemote ? 'REMOTE' : 'LOCAL'}] Animation source assignment for participant ${participantId}:`, {
      participantId,
      isRemote,
      hasParticipantData: !!participantData,
      participantWebcamOn: participantData?.webcamOn,
      hasParticipantWebcamStream: !!participantData?.webcamStream,
      hasVideoStream: !!videoStream,
      finalCameraStream: !!cameraStream,
      streamSource: videoStream ? 'created from participant' : 'none',
      animationSource: isRemote ? 
        'Remote VRM should use remote participant camera stream' :
        'Local VRM should use local participant camera stream',
      expectedBehavior: isRemote ? 
        'Remote VRM should animate ONLY with remote participant\'s camera movements via MediaPipe' : 
        'Local VRM should animate ONLY with LOCAL user\'s camera movements via MediaPipe'
    });
  }, [participantId, participantData, videoStream, cameraStream, isRemote]);
  
  // Instance-specific variables (not global)
  const vrmRef = useRef({
    currentVrm: null,
    renderer: null,
    scene: null,
    camera: null,
    controls: null,
    light: null,
    clock: null,
    animationId: null
  });

  // Animation function for this specific VRM instance
  const animateVRM = (results) => {
    if (isRemote) {
      // console.log("[VRM DEBUG] animateVRM called with", results);
    }
    if (!vrmRef.current.currentVrm) return;

    const faceLandmarks = results.faceLandmarks;
    const pose3DLandmarks = results.ea || results.poseLandmarks; // Handle both formats
    const pose2DLandmarks = results.poseLandmarks;
    const leftHandLandmarks = results.rightHandLandmarks;
    const rightHandLandmarks = results.leftHandLandmarks;

    // Handle face tracking
    if (faceLandmarks) {
      const riggedFace = Kalidokit.Face.solve(faceLandmarks, {
        runtime: "mediapipe",
        video: videoRef.current
      });
      rigFace(riggedFace);
    }
    
    // Handle body tracking
    if (pose2DLandmarks && pose3DLandmarks) {
      if (isRemote) {
        // console.log("[VRM DEBUG] Processing pose data:", { pose2DLandmarks: !!pose2DLandmarks, pose3DLandmarks: !!pose3DLandmarks });
      }
      
      const riggedPose = Kalidokit.Pose.solve(pose3DLandmarks, pose2DLandmarks, {
        runtime: "mediapipe",
        video: videoRef.current
      });

      if (isRemote) {
        // console.log("[VRM DEBUG] Rigged pose:", riggedPose);
      }

      // Animate main body parts
      rigRotation("Hips", riggedPose.Hips.rotation, 0.7);
      rigPosition(
        "Hips",
        {
          x: riggedPose.Hips.position.x,
          y: riggedPose.Hips.position.y + 1,
          z: -riggedPose.Hips.position.z
        },
        1,
        0.07
      );
      
      rigRotation("Chest", riggedPose.Spine, 0.25, 0.3);
      rigRotation("Spine", riggedPose.Spine, 0.45, 0.3);
      
      rigRotation("RightUpperArm", riggedPose.RightUpperArm, 1, 0.3);
      rigRotation("RightLowerArm", riggedPose.RightLowerArm, 1, 0.3);
      rigRotation("LeftUpperArm", riggedPose.LeftUpperArm, 1, 0.3);
      rigRotation("LeftLowerArm", riggedPose.LeftLowerArm, 1, 0.3);
      
      rigRotation("LeftUpperLeg", riggedPose.LeftUpperLeg, 1, 0.3);
      rigRotation("LeftLowerLeg", riggedPose.LeftLowerLeg, 1, 0.3);
      rigRotation("RightUpperLeg", riggedPose.RightUpperLeg, 1, 0.3);
      rigRotation("RightLowerLeg", riggedPose.RightLowerLeg, 1, 0.3);
      
      // Handle hand tracking
      if (leftHandLandmarks) {
        const riggedLeftHand = Kalidokit.Hand.solve(leftHandLandmarks, "Left");
        rigRotation("LeftHand", {
          z: riggedPose.LeftHand.z,
          y: riggedLeftHand.LeftWrist.y,
          x: riggedLeftHand.LeftWrist.x
        });
        
        // Animate left hand fingers
        rigRotation("LeftRingProximal", riggedLeftHand.LeftRingProximal);
        rigRotation("LeftRingIntermediate", riggedLeftHand.LeftRingIntermediate);
        rigRotation("LeftRingDistal", riggedLeftHand.LeftRingDistal);
        rigRotation("LeftIndexProximal", riggedLeftHand.LeftIndexProximal);
        rigRotation("LeftIndexIntermediate", riggedLeftHand.LeftIndexIntermediate);
        rigRotation("LeftIndexDistal", riggedLeftHand.LeftIndexDistal);
        rigRotation("LeftMiddleProximal", riggedLeftHand.LeftMiddleProximal);
        rigRotation("LeftMiddleIntermediate", riggedLeftHand.LeftMiddleIntermediate);
        rigRotation("LeftMiddleDistal", riggedLeftHand.LeftMiddleDistal);
        rigRotation("LeftThumbProximal", riggedLeftHand.LeftThumbProximal);
        rigRotation("LeftThumbIntermediate", riggedLeftHand.LeftThumbIntermediate);
        rigRotation("LeftThumbDistal", riggedLeftHand.LeftThumbDistal);
        rigRotation("LeftLittleProximal", riggedLeftHand.LeftLittleProximal);
        rigRotation("LeftLittleIntermediate", riggedLeftHand.LeftLittleIntermediate);
        rigRotation("LeftLittleDistal", riggedLeftHand.LeftLittleDistal);
      }
      
      if (rightHandLandmarks) {
        const riggedRightHand = Kalidokit.Hand.solve(rightHandLandmarks, "Right");
        rigRotation("RightHand", {
          z: riggedPose.RightHand.z,
          y: riggedRightHand.RightWrist.y,
          x: riggedRightHand.RightWrist.x
        });
        
        // Animate right hand fingers
        rigRotation("RightRingProximal", riggedRightHand.RightRingProximal);
        rigRotation("RightRingIntermediate", riggedRightHand.RightRingIntermediate);
        rigRotation("RightRingDistal", riggedRightHand.RightRingDistal);
        rigRotation("RightIndexProximal", riggedRightHand.RightIndexProximal);
        rigRotation("RightIndexIntermediate", riggedRightHand.RightIndexIntermediate);
        rigRotation("RightIndexDistal", riggedRightHand.RightIndexDistal);
        rigRotation("RightMiddleProximal", riggedRightHand.RightMiddleProximal);
        rigRotation("RightMiddleIntermediate", riggedRightHand.RightMiddleIntermediate);
        rigRotation("RightMiddleDistal", riggedRightHand.RightMiddleDistal);
        rigRotation("RightThumbProximal", riggedRightHand.RightThumbProximal);
        rigRotation("RightThumbIntermediate", riggedRightHand.RightThumbIntermediate);
        rigRotation("RightThumbDistal", riggedRightHand.RightThumbDistal);
        rigRotation("RightLittleProximal", riggedRightHand.RightLittleProximal);
        rigRotation("RightLittleIntermediate", riggedRightHand.RightLittleIntermediate);
        rigRotation("RightLittleDistal", riggedRightHand.RightLittleDistal);
      }
    }
  };

  // Helper function to rig rotation
  const rigRotation = (name, rotation = { x: 0, y: 0, z: 0 }, dampener = 1, lerpAmount = 0.3) => {
    if (!vrmRef.current.currentVrm) return;
    
    const Part = vrmRef.current.currentVrm.humanoid.getBoneNode(window.THREE.VRMSchema.HumanoidBoneName[name]);
    if (!Part) return;
    
    const euler = new window.THREE.Euler(
      rotation.x * dampener,
      rotation.y * dampener,
      rotation.z * dampener,
      rotation.rotationOrder || "XYZ"
    );
    const quaternion = new window.THREE.Quaternion().setFromEuler(euler);
    Part.quaternion.slerp(quaternion, lerpAmount);
  };

  // Helper function to rig position
  const rigPosition = (name, position = { x: 0, y: 0, z: 0 }, dampener = 1, lerpAmount = 0.3) => {
    if (!vrmRef.current.currentVrm) return;
    
    const Part = vrmRef.current.currentVrm.humanoid.getBoneNode(window.THREE.VRMSchema.HumanoidBoneName[name]);
    if (!Part) return;
    
    const vector = new window.THREE.Vector3(
      position.x * dampener,
      position.y * dampener,
      position.z * dampener
    );
    Part.position.lerp(vector, lerpAmount);
  };

  // Storage for look target
  const oldLookTarget = useRef(new window.THREE.Euler());
  
  // Rig facial expressions
  const rigFace = (riggedFace) => {
    if (!vrmRef.current.currentVrm) return;
    
    rigRotation("Neck", riggedFace.head, 0.7);
    
    const Blendshape = vrmRef.current.currentVrm.blendShapeProxy;
    const PresetName = window.THREE.VRMSchema.BlendShapePresetName;
    
    // Eye blinking
    riggedFace.eye.l = lerp(clamp(1 - riggedFace.eye.l, 0, 1), Blendshape.getValue(PresetName.Blink), 0.5);
    riggedFace.eye.r = lerp(clamp(1 - riggedFace.eye.r, 0, 1), Blendshape.getValue(PresetName.Blink), 0.5);
    riggedFace.eye = Kalidokit.Face.stabilizeBlink(riggedFace.eye, riggedFace.head.y);
    Blendshape.setValue(PresetName.Blink, riggedFace.eye.l);
    
    // Mouth expressions
    Blendshape.setValue(PresetName.I, lerp(riggedFace.mouth.shape.I, Blendshape.getValue(PresetName.I), 0.5));
    Blendshape.setValue(PresetName.A, lerp(riggedFace.mouth.shape.A, Blendshape.getValue(PresetName.A), 0.5));
    Blendshape.setValue(PresetName.E, lerp(riggedFace.mouth.shape.E, Blendshape.getValue(PresetName.E), 0.5));
    Blendshape.setValue(PresetName.O, lerp(riggedFace.mouth.shape.O, Blendshape.getValue(PresetName.O), 0.5));
    Blendshape.setValue(PresetName.U, lerp(riggedFace.mouth.shape.U, Blendshape.getValue(PresetName.U), 0.5));
    
    // Pupil control
    const lookTarget = new window.THREE.Euler(
      lerp(oldLookTarget.current.x, riggedFace.pupil.y, 0.4),
      lerp(oldLookTarget.current.y, riggedFace.pupil.x, 0.4),
      0,
      "XYZ"
    );
    oldLookTarget.current.copy(lookTarget);
    vrmRef.current.currentVrm.lookAt.applyer.lookAt(lookTarget);
  };

  // Set up MediaPipe tracking using VideoSDK webcam stream
  const setupStreamTracking = () => {
    if (!videoRef.current || !cameraStream) return;

    console.log(`Setting up MediaPipe tracking for ${isRemote ? 'remote' : 'local'} VRM using VideoSDK stream`);
    
    // Set the video source to the VideoSDK webcam stream
    videoRef.current.srcObject = cameraStream;
    
    videoRef.current.onloadedmetadata = () => {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log(`${isRemote ? 'Remote' : 'Local'} VRM - video is playing VideoSDK stream`);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(`${isRemote ? 'Remote' : 'Local'} VRM - Video play failed:`, error);
            }
          });
      }
    };
    
    // Create individual MediaPipe instance for this VRM to ensure isolation
    let animationFrameId = null;
    let isActive = true;
    let holisticInstance = null;
    
    const setupMediaPipe = async () => {
      try {
        if (!window.Holistic) {
          console.warn(`${isRemote ? 'Remote' : 'Local'} VRM - MediaPipe Holistic not loaded`);
          return;
        }

        console.log(`${isRemote ? 'Remote' : 'Local'} VRM - Creating individual MediaPipe instance`);
        setIsMediaPipeInitializing(true);
        
        // Create a separate Holistic instance for this VRM
        holisticInstance = new window.Holistic({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/holistic@0.5.1635989137/${file}`;
          },
        });

        // Configure the Holistic instance
        holisticInstance.setOptions({
          modelComplexity: 1,
          smoothLandmarks: true,
          enableSegmentation: false,
          smoothSegmentation: false,
          refineFaceLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5
        });

        // Set up result handler for this specific VRM
        holisticInstance.onResults((results) => {
          if (isActive && vrmRef.current.currentVrm) {
            try {
              animateVRM(results);
            } catch (error) {
              console.warn(`${isRemote ? 'Remote' : 'Local'} VRM - Animation error:`, error);
            }
          }
        });

        // Initialize the instance
        await holisticInstance.initialize();
        setIsMediaPipeInitializing(false);
        
        console.log(`${isRemote ? 'Remote' : 'Local'} VRM - MediaPipe instance ready`);
        
        // Start frame processing loop
        const processFrame = async () => {
          if (!isActive || !holisticInstance) return;
          if (videoRef.current && videoRef.current.readyState >= 2) {
            try {
              await holisticInstance.send({ image: videoRef.current });
            } catch (error) {
              console.warn(`${isRemote ? 'Remote' : 'Local'} VRM - Frame processing error:`, error);
            }
          }
          animationFrameId = requestAnimationFrame(processFrame);
        };
        
        processFrame();
        
      } catch (error) {
        console.error(`${isRemote ? 'Remote' : 'Local'} VRM - MediaPipe setup failed:`, error);
        setIsMediaPipeInitializing(false);
      }
    };
    
    setupMediaPipe();
    
    return () => {
      console.log(`${isRemote ? 'Remote' : 'Local'} VRM - Cleaning up stream processing`);
      isActive = false;
      setIsMediaPipeInitializing(false);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (holisticInstance) {
        try {
          holisticInstance.close();
        } catch (error) {
          console.warn(`${isRemote ? 'Remote' : 'Local'} VRM - Error closing MediaPipe instance:`, error);
        }
      }
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    };
  };

  useEffect(() => {
    const vrmData = vrmRef.current;
    let isComponentMounted = true;

    // One-time initialization for THREE.js scene
    const initializeScene = () => {
      if (vrmData.renderer) {
        console.log("Scene already initialized for this instance");
        return;
      }
      
      if (!containerRef.current) {
        console.log("Container not ready, retrying...");
        setTimeout(initializeScene, 100);
        return;
      }
      
      console.log("Initializing THREE.js scene for", isRemote ? "remote" : "local");
      
      // 1. Create renderer
      vrmData.renderer = new window.THREE.WebGLRenderer({
        alpha: true,
        canvas: containerRef.current
      });
      
      // Get the actual container dimensions
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      console.log("Container dimensions:", { width: containerWidth, height: containerHeight });
      
      // Use full dimensions like VRMStatic does
      vrmData.renderer.setSize(containerWidth || window.innerWidth * 0.5, containerHeight || window.innerHeight);
      vrmData.renderer.setPixelRatio(window.devicePixelRatio);
      
      // 2. Create camera
      vrmData.camera = new window.THREE.PerspectiveCamera(
        35, 
        (containerWidth || window.innerWidth * 0.5) / (containerHeight || window.innerHeight), 
        0.1, 
        1000
      );
      vrmData.camera.position.set(0.0, 1.4, 0.7);
      
      // 3. Create controls
      vrmData.controls = new window.THREE.OrbitControls(vrmData.camera, vrmData.renderer.domElement);
      vrmData.controls.screenSpacePanning = true;
      vrmData.controls.target.set(0.0, 1.4, 0.0);
      vrmData.controls.update();
      
      // 4. Create scene and add light
      vrmData.scene = new window.THREE.Scene();
      vrmData.light = new window.THREE.DirectionalLight(0xffffff);
      vrmData.light.position.set(1.0, 1.0, 1.0).normalize();
      vrmData.scene.add(vrmData.light);
      
      // Add ambient light for better visibility
      const ambientLight = new window.THREE.AmbientLight(0xffffff, 0.5);
      vrmData.scene.add(ambientLight);
      
      // 5. Create clock for animations
      vrmData.clock = new window.THREE.Clock();
      
      // 6. Handle window resize
      const handleResize = () => {
        if (!vrmData.camera || !vrmData.renderer || !containerRef.current) return;
        const newRect = containerRef.current.getBoundingClientRect();
        const newWidth = newRect.width;
        const newHeight = newRect.height;
        vrmData.camera.aspect = newWidth / newHeight;
        vrmData.camera.updateProjectionMatrix();
        vrmData.renderer.setSize(newWidth, newHeight);
      };
      window.addEventListener('resize', handleResize);
      
      return () => window.removeEventListener('resize', handleResize);
    };
    
    // Clear any existing models before loading a new one
    const clearScene = () => {
      if (!vrmData.scene) return;
      
      // Remove any VRM model
      if (vrmData.currentVrm && vrmData.currentVrm.scene) {
        vrmData.scene.remove(vrmData.currentVrm.scene);
        vrmData.currentVrm = null;
      }
      
      // Remove anything that's not a light
      vrmData.scene.children = vrmData.scene.children.filter(child => 
        child instanceof window.THREE.DirectionalLight || child instanceof window.THREE.AmbientLight
      );
      
      console.log("Scene cleared, objects remaining:", vrmData.scene.children.length);
    };
    
    // Load the VRM model
    const loadVRMModel = () => {
      if (!vrmData.scene) return;
      
      // Remove any existing models
      clearScene();
      
      const vrmPath = selectedAvatar || avatarAssets.getDefaultAvatar().vrmPath;
      
      console.log("Loading VRM model:", vrmPath);
      setIsModelLoaded(false);
      
      // Use traditional loading approach like VRMStatic
      const loader = new window.THREE.GLTFLoader();
      loader.crossOrigin = 'anonymous';
      
      loader.load(
        vrmPath,
        (gltf) => {
          if (!isComponentMounted) return;
          
          console.log("VRM model loaded successfully:", vrmPath);
          window.THREE.VRMUtils.removeUnnecessaryJoints(gltf.scene);
          
          window.THREE.VRM.from(gltf).then((vrm) => {
            if (!isComponentMounted) return;
            
            // Make sure we don't already have a model
            clearScene();
            
            // Add model to scene
            vrmData.scene.add(vrm.scene);
            vrmData.currentVrm = vrm;
            vrmData.currentVrm.scene.rotation.y = Math.PI; // Face camera
            console.log("VRM model added to scene successfully");
            setIsModelLoaded(true);
            
            // Log scene contents for debugging
            console.log("Scene children after loading:", vrmData.scene.children.length);
            vrmData.scene.children.forEach((child, i) => {
              console.log(`  Child ${i}:`, child.type);
            });
          }).catch((error) => {
            console.error("Error creating VRM from gltf:", error);
            setIsModelLoaded(false);
          });
        },
        (progress) => console.log("Loading: ", 100.0 * (progress.loaded / progress.total), "%"),
        (error) => {
          console.error("Error loading VRM model:", error);
          setIsModelLoaded(false);
        }
      );
    };
    
    // Set up animation loop
    const startAnimation = () => {
      // Stop any existing animation
      if (vrmData.animationId) {
        cancelAnimationFrame(vrmData.animationId);
        vrmData.animationId = null;
      }
      
      const animate = () => {
        // Stop if component unmounted
        if (!isComponentMounted || !containerRef.current) return;
        
        vrmData.animationId = requestAnimationFrame(animate);
        
        if (vrmData.currentVrm && vrmData.clock) {
          vrmData.currentVrm.update(vrmData.clock.getDelta());
        }
        
        if (vrmData.renderer && vrmData.scene && vrmData.camera) {
          vrmData.renderer.render(vrmData.scene, vrmData.camera);
        }
      };
      
      animate();
    };

    // Check dependencies and initialize
    const checkDependencies = () => {
      return window.THREE && window.THREE.VRM;
    };

    const tryInitialize = () => {
      if (!checkDependencies()) {
        console.log("Dependencies not ready, retrying...");
        setTimeout(tryInitialize, 100);
        return;
      }

      console.log("All dependencies loaded, initializing VRM scene...");
      initializeScene();
      loadVRMModel();
      startAnimation();
    };

    tryInitialize();

    // Cleanup function
    return () => {
      console.log(`[VRM ${isRemote ? 'REMOTE' : 'LOCAL'}] Cleaning up VRM scene and model`);
      isComponentMounted = false;
      
      if (vrmData.animationId) {
        cancelAnimationFrame(vrmData.animationId);
      }
      
      if (vrmData.scene && vrmData.currentVrm) {
        vrmData.scene.remove(vrmData.currentVrm.scene);
        if (vrmData.currentVrm.dispose) {
          vrmData.currentVrm.dispose();
        }
      }
      
      if (vrmData.renderer) {
        vrmData.renderer.dispose();
      }
    };
  }, [selectedAvatar, isRemote]); // Only re-run when avatar or remote flag changes

  // Local VRM: Use MediaPipe to process camera stream
  useEffect(() => {
    let streamCleanup = null;
    
    // Both local and remote VRMs should use MediaPipe with their respective camera streams
    if (cameraStream && window.Holistic && isModelLoaded) {
      console.log(`[VRM ${isRemote ? 'REMOTE' : 'LOCAL'}] Setting up MediaPipe stream tracking (VRM scene persists)`);
      streamCleanup = setupStreamTracking();
    }
    
    return () => {
      if (streamCleanup) {
        console.log(`[VRM ${isRemote ? 'REMOTE' : 'LOCAL'}] Cleaning up MediaPipe stream tracking (VRM scene persists)`);
        streamCleanup();
      }
    };
  }, [cameraStream, isModelLoaded, isRemote]); // Both local and remote VRMs process their respective camera streams

  return (
    <div className="vrm-viewer-container">
      {/* Simple Loading Overlay */}
      {!isModelLoaded && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.2)',
          zIndex: 150
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div 
              className="vrm-loading-spinner"
              style={{
                width: '30px',
                height: '30px',
                border: '3px solid rgba(255, 255, 255, 0.3)',
                borderTop: '3px solid #ff8a00',
                borderRadius: '50%'
              }}
            />
            <div style={{
              color: 'white',
              fontSize: '12px',
              textShadow: '0 1px 2px rgba(0,0,0,0.7)'
            }}>
              Loading...
            </div>
          </div>
        </div>
      )}

      {/* MediaPipe Initialization Spinner */}
      {isModelLoaded && isMediaPipeInitializing && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.3)',
          zIndex: 140
        }}>
          <div 
            style={{
              width: '24px',
              height: '24px',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              borderTop: '3px solid #00ff88',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}
          />
        </div>
      )}
      
      {/* <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        color: 'white',
        background: 'rgba(0,0,0,0.7)',
        padding: '5px',
        zIndex: 100,
        fontSize: '12px'
      }}>
        VRM {isRemote ? '(Remote)' : '(Local)'} - {isModelLoaded ? 'Loaded' : 'Loading...'}
        {cameraStream ? ' - Camera Ready' : ' - No Camera'}
      </div> */}
      <video 
        ref={videoRef} 
        playsInline 
        className="input_video" 
        style={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none'
        }}
      />
      <canvas 
        ref={containerRef}
        className="vrm-canvas"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          background: 'transparent',
          position: 'relative',
          top: 0,
          left: 0,
          zIndex: 1
        }}
      />
    </div>
  );
};

export default SingleVRMViewer;
